import { epochLabel, licenceOf, resolutionText, source } from '../catalogue';
import type { StackEntry } from '../types';
import type { AppState } from '../state';
import { clear, el } from './dom';

export interface HudHandlers {
  onOpenPicker: () => void;
  onLocate: () => void;
  onToggleCompare: () => void;
  onExport: () => void;
  onOpacity: (sourceId: string, value: number) => void;
  onRemoveOverlay: (sourceId: string) => void;
  onInfo: (sourceId: string, timeId?: string) => void;
}

export class Hud {
  readonly root = el('div', { class: 'hud' });
  private readonly stack = el('div', { class: 'hud-stack' });
  private readonly attribution = el('div', { class: 'hud-attribution' });
  private readonly coords = el('div', { class: 'hud-coords', attrs: { hidden: 'hidden' } });
  private readonly scaleBar = el('div', { class: 'hud-scale-bar' });
  private readonly scaleLabel = el('span', { text: '' });
  private readonly scale = el('div', { class: 'hud-scale' }, [this.scaleBar, this.scaleLabel]);
  private readonly compareBtn: HTMLButtonElement;
  private readonly exportBtn: HTMLButtonElement;

  setExportActive(on: boolean): void {
    this.exportBtn.classList.toggle('is-on', on);
  }

  constructor(private readonly handlers: HudHandlers, timestrip: HTMLElement) {
    this.compareBtn = el('button', {
      class: 'hud-btn',
      attrs: { type: 'button', 'aria-label': 'Porovnat vrstvy' },
      text: '◑',
      on: { click: () => this.handlers.onToggleCompare() },
    });

    this.exportBtn = el('button', {
      class: 'hud-btn',
      attrs: { type: 'button', 'aria-label': 'Export výřezu' },
      text: '⤓',
      on: { click: () => this.handlers.onExport() },
    });

    const buttons = el('div', { class: 'hud-buttons' }, [
      el('button', {
        class: 'hud-btn hud-btn-primary',
        attrs: { type: 'button' },
        text: 'Zdroje',
        on: { click: () => this.handlers.onOpenPicker() },
      }),
      this.compareBtn,
      this.exportBtn,
      el('button', {
        class: 'hud-btn',
        attrs: { type: 'button', 'aria-label': 'Moje poloha' },
        text: '◎',
        on: { click: () => this.handlers.onLocate() },
      }),
    ]);

    this.root.append(this.scale, this.coords, timestrip, this.stack, buttons, this.attribution);
  }

  /**
   * Our own scale bar. OpenLayers' ScaleLine control would do this, but styling it
   * out of its default corner made Chromium composite a full-height translucent
   * layer over the map — visible as a pale band. Drawing it ourselves avoids
   * fighting ol.css at all.
   */
  setScale(metresPerPixel: number): void {
    const candidates = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000];
    const maxPx = 110;
    let metres = candidates[0];
    for (const c of candidates) {
      if (c / metresPerPixel <= maxPx) metres = c;
    }
    const px = Math.round(metres / metresPerPixel);
    this.scaleBar.style.width = `${px}px`;
    this.scaleLabel.textContent = metres >= 1000 ? `${metres / 1000} km` : `${metres} m`;
  }

  showCoords(lon: number, lat: number, sjtsk: [number, number]): void {
    this.coords.hidden = false;
    this.coords.textContent =
      `${lat.toFixed(5)}, ${lon.toFixed(5)} · S-JTSK ${Math.round(sjtsk[0])}, ${Math.round(sjtsk[1])}`;
    clearTimeout(this.coordTimer);
    this.coordTimer = setTimeout(() => (this.coords.hidden = true), 6000);
  }

  private coordTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly rows = new Map<string, { root: HTMLElement; slider: HTMLInputElement }>();
  private renderedKeys: string[] = [];

  private buildRow(entry: StackEntry, isBase: boolean): HTMLElement {
    const def = source(entry.sourceId)!;
    const slider = el('input', {
      class: 'hud-opacity',
      attrs: {
        type: 'range',
        min: '0',
        max: '100',
        value: String(Math.round(entry.opacity * 100)),
        'aria-label': `Průhlednost — ${def.title}`,
      },
      on: {
        input: (ev: Event) =>
          this.handlers.onOpacity(entry.sourceId, Number((ev.target as HTMLInputElement).value) / 100),
      },
    });

    const root = el('div', { class: `hud-layer${isBase ? ' is-base' : ''}` }, [
      el('div', { class: 'hud-layer-head' }, [
        el('button', {
          class: 'hud-layer-name',
          attrs: { type: 'button' },
          on: { click: () => this.handlers.onInfo(entry.sourceId, entry.timeId) },
        }, [
          el('span', { class: 'hud-layer-title', text: def.title }),
          el('span', {
            class: 'hud-layer-meta',
            text: [resolutionText(def, entry.timeId), epochLabel(def, entry.timeId)]
              .filter(Boolean)
              .join(' · '),
          }),
        ]),
        isBase
          ? null
          : el('button', {
              class: 'hud-layer-remove',
              attrs: { type: 'button', 'aria-label': 'Odebrat překryv' },
              text: '✕',
              on: { click: () => this.handlers.onRemoveOverlay(entry.sourceId) },
            }),
      ]),
      slider,
    ]);

    this.rows.set(entry.sourceId, { root, slider });
    return root;
  }

  update(state: AppState): void {
    const entries: { entry: StackEntry; isBase: boolean }[] = [
      ...[...state.overlays].reverse().map((entry) => ({ entry, isBase: false })),
      { entry: state.base, isBase: true },
    ].filter(({ entry }) => source(entry.sourceId));

    // Rebuild only when the stack itself changed. Re-creating rows on every state
    // change would rip out whatever control the user is currently dragging.
    const keys = entries.map(({ entry, isBase }) => `${entry.sourceId}|${entry.timeId ?? ''}|${isBase}`);
    const changed =
      keys.length !== this.renderedKeys.length || keys.some((k, i) => k !== this.renderedKeys[i]);
    if (changed) {
      clear(this.stack);
      this.rows.clear();
      for (const { entry, isBase } of entries) {
        this.stack.append(this.buildRow(entry, isBase));
      }
      this.renderedKeys = keys;
    }

    for (const { entry } of entries) {
      const row = this.rows.get(entry.sourceId);
      if (!row) continue;
      const percent = String(Math.round(entry.opacity * 100));
      // Never write over a slider the user has hold of.
      if (row.slider.value !== percent && document.activeElement !== row.slider) {
        row.slider.value = percent;
      }
    }

    this.compareBtn.classList.toggle('is-on', state.compare);
    this.compareBtn.disabled = state.overlays.length === 0;

    clear(this.attribution);
    const seen = new Set<string>();
    for (const { entry } of entries) {
      const def = source(entry.sourceId);
      if (!def || seen.has(def.attribution)) continue;
      seen.add(def.attribution);
      const lic = licenceOf(def);
      this.attribution.append(
        el('span', { class: 'hud-attr-item' }, [
          el('span', { text: def.attribution }),
          el('a', {
            class: lic.free ? 'hud-attr-lic' : 'hud-attr-lic is-restricted',
            text: lic.label,
            attrs: { href: lic.url, target: '_blank', rel: 'noreferrer noopener' },
          }),
        ]),
      );
    }
  }
}
